import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useBrands } from '../hooks/useProducts';
import { getBrandLogoUrl } from '../lib/utils';

const FeaturedBrands: React.FC = () => {
  const { brands: featuredBrands, loading, error } = useBrands(true); // Only fetch featured brands

  if (loading) {
    return (
      <section className="py-12 bg-white">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-black">Featured Brands</h2>
            <p className="mt-2 text-gray-600 max-w-2xl mx-auto">
              Discover premium instruments from the world's most trusted music brands.
            </p>
          </div>
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-red-700" />
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-12 bg-white">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-black">Featured Brands</h2>
            <p className="mt-2 text-gray-600 max-w-2xl mx-auto">
              Discover premium instruments from the world's most trusted music brands.
            </p>
          </div>
          <div className="text-center py-12">
            <p className="text-red-600">Error loading featured brands: {error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-white">
      <div className="container-custom">
        <div className="text-center mb-10">
          <motion.h2 
            className="text-3xl font-bold text-black"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Featured Brands
          </motion.h2>
          <motion.p 
            className="mt-2 text-gray-600 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Discover premium instruments from the world's most trusted music brands.
          </motion.p>
        </div>

        {featuredBrands.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {featuredBrands.map((brand, index) => (
              <motion.div 
                key={brand.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <Link 
                  to={`/products?brand=${brand.id}`}
                  className="block bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg hover:border-red-300 transition-all duration-300 text-center"
                >
                  {/* Brand Logo */}
                  <div className="aspect-square mb-4 flex items-center justify-center bg-gray-50 rounded-lg overflow-hidden">
                    <img 
                      src={getBrandLogoUrl(brand.logo_url)}
                      alt={`${brand.name} logo`}
                      className="max-w-full max-h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        // Fallback to a placeholder if brand logo fails to load
                        e.currentTarget.src = 'https://images.pexels.com/photos/164743/pexels-photo-164743.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&dpr=1';
                      }}
                    />
                  </div>
                  
                  {/* Brand Name */}
                  <h3 className="font-bold text-lg text-black group-hover:text-red-700 transition-colors mb-2">
                    {brand.name}
                  </h3>
                  
                  {/* Brand Description */}
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {brand.description}
                  </p>
                  
                  {/* Country */}
                  {brand.country && (
                    <p className="text-xs text-gray-500 mt-1">
                      {brand.country}
                    </p>
                  )}
                  
                  {/* View Products Link */}
                  <div className="mt-3 text-sm font-medium text-red-700 group-hover:text-red-800 transition-colors">
                    View Products →
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">No featured brands available at the moment.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedBrands;